import type {
  AdminDepartureDestination,
  createAdminDepartureCoordinator,
} from "./adminDepartureCoordinator";
import { isAdminSignOutLocked } from "./adminSignOutLock";

type Coordinator = ReturnType<typeof createAdminDepartureCoordinator>;

export interface AdminDepartureHistoryBrowser {
  readonly history: { readonly state: unknown; go(delta: number): void };
  readonly location: { readonly href: string };
  addEventListener(
    type: "popstate",
    listener: (event: PopStateEvent) => void,
    capture: boolean,
  ): void;
  removeEventListener(
    type: "popstate",
    listener: (event: PopStateEvent) => void,
    capture: boolean,
  ): void;
}

interface Entry {
  index: number;
  key: string;
  url: string;
}
interface Observed {
  entry: Entry;
  state: unknown;
}
type Phase = "restoring" | "consenting" | "replaying" | "recovering";
interface Movement {
  source: Entry;
  target: Observed;
  phase: Phase;
  blockedBySignOut: boolean;
  validity: () => boolean;
  proceed: () => void;
}

const same = (
  a: Entry | null | undefined,
  b: Entry | null | undefined,
): boolean =>
  !!a && !!b && a.index === b.index && a.key === b.key && a.url === b.url;

function identity(state: unknown, url: string): Entry | null {
  if (!state || typeof state !== "object") return null;
  const record = state as Record<string, unknown>;
  if (
    typeof record.idx !== "number" ||
    !Number.isSafeInteger(record.idx) ||
    record.idx < 0
  )
    return null;
  // React Router initializes its first entry with idx: 0 and a default location key.
  const key =
    record.key === undefined && record.idx === 0 ? "default" : record.key;
  return typeof key === "string" && key.length > 0
    ? { index: record.idx, key, url }
    : null;
}

function routerState(state: unknown): unknown {
  if (!state || typeof state !== "object") return null;
  return (state as Record<string, unknown>).usr ?? null;
}

/**
 * Install before BrowserRouter; call sync after its initial setup and every push/replace.
 * No pushState/replaceState is used: native state and history entries stay intact.
 * Missing/conflicting indexes or unexpected in-flight events quarantine router POPs.
 * In that case the address may differ from the rendered source; no exact restoration
 * is claimed. Only an explicit sync after a router-owned write clears quarantine.
 */
export function createAdminDepartureHistory(
  coordinator: Coordinator,
  browser: AdminDepartureHistoryBrowser = window,
  isInteractionLocked: () => boolean = isAdminSignOutLocked,
) {
  const known = new Map<number, Entry>();
  let source: Entry | null = null;
  let movement: Movement | null = null;
  let installed = false;
  let stopping = false;
  let blocked = false;
  const restorationWaiters = new Set<(restored: boolean) => void>();
  const settleRestoration = (restored: boolean) => {
    [...restorationWaiters].forEach((settle) => settle(restored));
  };

  const read = (event?: PopStateEvent): Observed | null => {
    try {
      const url = new URL(browser.location.href).href;
      const state = browser.history.state;
      const entry = identity(state, url);
      if (!entry || (event && !same(entry, identity(event.state, url))))
        return null;
      return { entry, state };
    } catch {
      return null;
    }
  };
  const compatible = (entry: Entry): boolean => {
    const existing = known.get(entry.index);
    if (existing && !same(existing, entry)) return false;
    return [...known.values()].every(
      (other) => other.key !== entry.key || same(other, entry),
    );
  };
  const destination = (observed: Observed): AdminDepartureDestination => {
    const url = new URL(observed.entry.url);
    return {
      kind: "pop",
      to: url.pathname + url.search + url.hash,
      state: routerState(observed.state),
    };
  };
  const detach = () => {
    browser.removeEventListener("popstate", onPop, true);
    installed = false;
    stopping = false;
    settleRestoration(false);
  };
  const quarantine = (transaction: Movement | null = movement) => {
    blocked = true;
    movement = null;
    if (transaction) coordinator.cancelPending(transaction.proceed);
    settleRestoration(false);
  };
  const traverse = (delta: number, transaction: Movement) => {
    // History.go uses a signed Web IDL long; refuse a delta it would wrap.
    if (
      !Number.isSafeInteger(delta) ||
      delta === 0 ||
      Math.abs(delta) > 2_147_483_647
    ) {
      quarantine(transaction);
      return;
    }
    try {
      browser.history.go(delta);
    } catch {
      quarantine(transaction);
    }
  };
  const finishAtSource = (transaction: Movement) => {
    source = transaction.source;
    movement = null;
    settleRestoration(true);
    if (stopping) detach();
  };
  const recover = (transaction: Movement, observed: Observed | null) => {
    coordinator.cancelPending(transaction.proceed);
    if (same(observed?.entry, transaction.source)) {
      finishAtSource(transaction);
    } else if (
      observed &&
      compatible(observed.entry) &&
      compatible(transaction.source) &&
      observed.entry.index !== transaction.source.index
    ) {
      transaction.phase = "recovering";
      movement = transaction;
      traverse(transaction.source.index - observed.entry.index, transaction);
    } else quarantine(transaction);
  };
  const consent = (transaction: Movement) => {
    if (stopping || transaction.blockedBySignOut || isInteractionLocked()) {
      finishAtSource(transaction);
      return;
    }
    const target = destination(transaction.target);
    transaction.phase = "consenting";
    const result = coordinator.request(target, transaction.proceed);
    // Synchronous approval has already moved into replaying; never overwrite it.
    if (result === "blocked" && movement === transaction) movement = null;
  };

  function onPop(event: PopStateEvent): void {
    if (!installed) return;
    const observed = read(event);
    if (blocked) {
      event.stopImmediatePropagation();
      return;
    }
    if (movement?.phase === "consenting" && !coordinator.pending)
      movement = null;
    if (movement) {
      const transaction = movement;
      if (
        transaction.phase === "replaying" &&
        !stopping &&
        !isInteractionLocked() &&
        observed &&
        same(observed.entry, transaction.target.entry) &&
        compatible(transaction.source) &&
        compatible(observed.entry) &&
        transaction.validity()
      ) {
        source = observed.entry;
        movement = null;
        return; // This exact replay bypass is consumed once, before the router sees it.
      }
      event.stopImmediatePropagation();
      if (
        transaction.phase === "restoring" ||
        transaction.phase === "recovering"
      ) {
        if (
          !observed ||
          !same(observed.entry, transaction.source) ||
          !compatible(observed.entry)
        ) {
          quarantine(transaction);
          return;
        }
        if (transaction.phase === "recovering") finishAtSource(transaction);
        else consent(transaction);
      } else recover(transaction, observed);
      return;
    }
    const address = new URL(browser.location.href);
    const target = observed
      ? destination(observed)
      : {
          kind: "pop" as const,
          to: address.pathname + address.search + address.hash,
          state: routerState(browser.history.state),
        };
    if (
      !isInteractionLocked() &&
      !coordinator.needsInterception(target) &&
      !stopping
    ) {
      source = observed?.entry ?? null;
      if (source && compatible(source)) known.set(source.index, source);
      return;
    }
    event.stopImmediatePropagation();
    if (
      !observed ||
      !source ||
      !compatible(source) ||
      !compatible(observed.entry) ||
      source.index === observed.entry.index
    ) {
      quarantine();
      return;
    }
    known.set(observed.entry.index, observed.entry);
    const transaction: Movement = {
      source,
      target: observed,
      phase: "restoring",
      blockedBySignOut: isInteractionLocked(),
      validity: () => false,
      proceed: () => {
        if (!installed || movement !== transaction) return;
        // The coordinator has just validated every approval. Bind replay to that
        // approved state, including any explicitly acknowledged save transition.
        transaction.validity = coordinator.captureValidity(
          destination(transaction.target),
        );
        const current = read();
        if (
          stopping ||
          isInteractionLocked() ||
          !same(current?.entry, transaction.source) ||
          !transaction.validity()
        ) {
          recover(transaction, current);
          return;
        }
        transaction.phase = "replaying";
        traverse(
          transaction.target.entry.index - transaction.source.index,
          transaction,
        );
      },
    };
    movement = transaction;
    traverse(source.index - observed.entry.index, transaction);
  }

  return {
    install(): void {
      if (installed) return;
      installed = true;
      stopping = false;
      browser.addEventListener("popstate", onPop, true);
    },
    /** In-flight traversals retain interception until the source is restored. */
    uninstall(): void {
      if (!installed) return;
      // Unknown in-flight traversals cannot safely be exposed to a mounted router.
      if (blocked) {
        stopping = true;
        return;
      }
      if (movement) {
        coordinator.cancelPending(movement.proceed);
        if (movement.phase !== "consenting") {
          stopping = true;
          return;
        }
        movement = null;
      }
      detach();
    },
    /**
     * Sign-out keeps its input lock until an already queued POP is restored.
     * The bound is a UI recovery limit, not a claim about native browser latency.
     * A stalled/unknown movement is quarantined before callers unlock the page.
     */
    waitForRestoration(timeoutMs = 5_000): Promise<boolean> {
      if (blocked) return Promise.resolve(false);
      if (movement?.phase === "consenting" && !coordinator.pending) {
        coordinator.cancelPending(movement.proceed);
        movement = null;
      }
      if (!movement) return Promise.resolve(true);
      return new Promise((resolve) => {
        const settle = (restored: boolean) => {
          if (!restorationWaiters.delete(settle)) return;
          window.clearTimeout(timer);
          resolve(restored);
        };
        restorationWaiters.add(settle);
        const timer = window.setTimeout(() => {
          quarantine();
        }, timeoutMs);
      });
    },
    sync(): boolean {
      const observed = read();
      if (movement) {
        if (
          movement.phase === "consenting" &&
          same(observed?.entry, movement.source)
        )
          return true;
        if (movement.phase === "consenting" && !coordinator.pending) {
          // Cancellation leaves no live consent. A suspended save keeps its
          // source above, but a committed router write retires that old retry.
          coordinator.cancelPending(movement.proceed);
          movement = null;
        } else {
          quarantine();
          return false;
        }
      }
      if (!observed) {
        source = null;
        blocked = true;
        return false;
      }
      // sync explicitly authorizes a router push/replace at this index.
      known.delete(observed.entry.index);
      if (!compatible(observed.entry)) {
        source = null;
        blocked = true;
        return false;
      }
      known.set(observed.entry.index, observed.entry);
      source = observed.entry;
      blocked = false;
      if (stopping) detach();
      return true;
    },
    get status(): Phase | "idle" | "blocked" {
      return blocked ? "blocked" : (movement?.phase ?? "idle");
    },
  };
}
